import {
  Alert,
  Button,
  Group,
  Image,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { getHotkeyHandler } from '@mantine/hooks'
import { IconAlertOctagon, IconArrowRight, IconCircleCheck, IconDeviceMobile } from '@tabler/icons-react'
import { zod4Resolver } from 'mantine-form-zod-resolver'
import { observer } from 'mobx-react-lite'
import { usePostHog } from 'posthog-js/react'
import { parse } from 'qs'
import { useMemo, useState } from 'react'
import { useSearch } from 'wouter'
import { z } from 'zod'

import logoImage from '@/assets/images/Logo-light-punchout.png'
import { useGlobalState } from '@/hooks/useGlobalState'
import { navigateTo } from '@/utils/navigation'
import { getAppSignInUrl } from '@/utils/sponsors'

const LoginSchema = z.object({
  email: z.email({ error: 'Invalid email address' }),
  password: z.string().min(6, 'Must be at least 6 characters long'),
})

function getPlatform(): 'ios' | 'android' | 'web' {
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return 'web'
}

export const LoginScreen = observer(function LoginScreen() {
  const posthog = usePostHog()
  const searchParamsString = useSearch()
  const searchParams: { email?: string; channelDeleted?: string; channelName?: string } = parse(searchParamsString)

  const { authenticationStore, configStore, uiStore } = useGlobalState()
  const [authError, setAuthError] = useState<string | null>(null)
  const [endUser, setEndUser] = useState<{ email: string } | null>(null)
  const [channelDeletedNotice, setChannelDeletedNotice] = useState<{ name: string } | null>(() =>
    searchParams.channelDeleted === '1' ? { name: (searchParams.channelName ?? '').trim() } : null,
  )

  const form = useForm<z.infer<typeof LoginSchema>>({
    mode: 'controlled',
    validate: zod4Resolver(LoginSchema),
    initialValues: {
      email: searchParams.email ?? '',
      password: '',
    },
  })

  const { logIn } = authenticationStore

  const onKeyboardEnter = useMemo(() => getHotkeyHandler([['Enter', () => void submitForm()]]), [])

  async function submitForm() {
    setAuthError(null)

    const validations = form.validate()

    if (validations.hasErrors) {
      setAuthError('Double check your email and password.')
      return
    }

    uiStore.showLoadingOverlay('relative')

    const response = await logIn(form.getValues())

    uiStore.hideLoadingOverlay()

    if (response.endUser) {
      posthog?.capture('portal_end_user_redirect', {
        userId: response.endUser.userId,
        referrer: document.referrer,
        entryUrl: window.location.href,
        platform: getPlatform(),
        skPlatform: 'portal',
      })
      setEndUser({ email: form.getValues().email })
      return
    }

    if (!response.ok) {
      if (response.code === 'NEW_PASSWORD_REQUIRED') {
        navigateTo('changePassword', {
          searchParams: {
            email: form.getValues().email,
            oldPassword: form.getValues().password,
          },
        })
        return
      }

      setAuthError(response.message)
    } else if (response.message === 'Sponsor selection required') {
      // User will be redirected to sponsor selection screen by route logic
      return
    } else {
      posthog?.capture('login_success', { $set: { email: form.getValues().email }, skPlatform: 'portal' })
    }
  }

  function restoreForm() {
    form.setValues({ email: '', password: '' })
    form.clearErrors()
    setAuthError(null)
    setEndUser(null)
  }

  return (
    <Stack gap="xxl" w="100%">
      <Image src={logoImage} alt="Sidekick Platform" fit="contain" w="100%" h="auto" />
      <Title order={1} size="h6" ta="center" fw={500}>
        Program Manager and Expert Portal
      </Title>

      {endUser ? (
        <Stack gap="lg">
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <ThemeIcon size="lg" radius="md" variant="light">
              <IconDeviceMobile />
            </ThemeIcon>
            <Title order={2} size="h4" fw={600}>
              Your account uses the Sidekick app
            </Title>
          </Group>

          <Text c="dimmed">
            The Sidekick Platform Portal is for experts and program managers who create content. Your account is set up
            to use the Sidekick Platform app.
          </Text>

          <Paper withBorder p="sm" radius="md" bg="var(--mantine-color-gray-0)">
            <Text size="xs" c="dimmed">
              Email address
            </Text>
            <Text fw={500}>{endUser.email}</Text>
          </Paper>

          <Button
            size="lg"
            fullWidth
            rightSection={<IconArrowRight size={18} />}
            onClick={() => window.location.assign(getAppSignInUrl(configStore.authBaseUrl, endUser.email))}>
            Go to the Sidekick App
          </Button>

          <Button variant="subtle" size="compact-lg" onClick={restoreForm} style={{ alignSelf: 'center' }}>
            Use a Different Email
          </Button>
        </Stack>
      ) : (
        <>
          {channelDeletedNotice && (
            <Alert
              variant="light"
              color="green"
              icon={<IconCircleCheck />}
              withCloseButton
              onClose={() => setChannelDeletedNotice(null)}
              title={
                channelDeletedNotice.name
                  ? `Channel "${channelDeletedNotice.name}" was successfully deleted.`
                  : 'Your channel was successfully deleted.'
              }>
              You have been logged out. Sign in to access another channel or contact support if this was unexpected.
            </Alert>
          )}

          {authError && <Alert variant="light" color="red" title={authError} icon={<IconAlertOctagon />}></Alert>}

          <TextInput withAsterisk label="Email Address" onKeyDown={onKeyboardEnter} {...form.getInputProps('email')} />
          <PasswordInput
            withAsterisk
            label="Password"
            onKeyDown={onKeyboardEnter}
            {...form.getInputProps('password')}
          />

          <Stack align="center">
            <Button size="lg" onClick={() => void submitForm()} fullWidth>
              Login
            </Button>

            <Group gap="md" justify="center">
              <Button variant="subtle" size="compact-lg" onClick={() => navigateTo('forgotPassword')}>
                Forgot Password?
              </Button>

              <Button
                variant="subtle"
                size="compact-lg"
                onClick={() =>
                  navigateTo('changePassword', {
                    searchParams: form.getValues().email ? { email: form.getValues().email } : null,
                  })
                }>
                Change Password
              </Button>
            </Group>
          </Stack>
        </>
      )}
    </Stack>
  )
})
